import { useState, useEffect, useMemo } from 'react';
import { StatusBadge, Button } from '../components/common';
import { calculateTotalEarnings, calculatePendingEarnings } from '../services/commissionService';
import { calculateMilestoneInfo, loadClaimedMilestones, calculateTotalMilestoneEarnings } from '../services/milestoneService';
import { supabase } from '../services/supabaseClient';

/**
 * My Earnings Page
 * For Marketers & Reps to view their commissions
 */
export function MyEarningsPage({ userCommissions, loadingCommissions, currentUser }) {
  const [closedDealsCount, setClosedDealsCount] = useState(0);
  const [claimedMilestones, setClaimedMilestones] = useState([]);
  const [milestoneInfo, setMilestoneInfo] = useState(null);

  // Load milestone data
  useEffect(() => {
    const loadData = async () => {
      if (supabase && currentUser && currentUser.role === 'marketer') {
        // Load closed deals count for this user
        const { count } = await supabase
          .from('restaurants')
          .select('*', { count: 'exact', head: true })
          .eq('assigned_to', currentUser.id)
          .eq('status', 'deal_closed');
        
        setClosedDealsCount(count || 0);
        
        // Load claimed milestones
        const claimed = await loadClaimedMilestones(currentUser.id);
        setClaimedMilestones(claimed);
        
        // Calculate milestone info
        setMilestoneInfo(calculateMilestoneInfo(count || 0));
      }
    };
    
    loadData();
  }, [currentUser]);
  
  // Calculate totals
  const totalEarnings = useMemo(() => 
    calculateTotalEarnings(userCommissions),
    [userCommissions]
  );

  const pendingEarnings = useMemo(() => 
    calculatePendingEarnings(userCommissions),
    [userCommissions]
  );

  const milestoneEarnings = useMemo(() => 
    calculateTotalMilestoneEarnings(claimedMilestones),
    [claimedMilestones]
  );

  const totalEarningsWithMilestones = totalEarnings + milestoneEarnings;

  if (loadingCommissions) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#000000" }}>
        جاري تحميل العمولات...
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: "#000000", fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          💰 محفظتي
        </h1>
        <p style={{ color: "#000000", fontSize: 14 }}>
          عرض العمولات والأرباح
        </p>
      </div>

      {/* Milestone Progress Bar (Marketers Only) */}
      {milestoneInfo && currentUser?.role === 'marketer' && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #0f172a)',
          borderRadius: 16,
          padding: 24,
          marginBottom: 24,
          border: '2px solid #4ecdc4',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 16 }}>
            <div>
              <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, margin: 0 }}>
                🎮 مكافآت الإنجازات
              </h3>
              <p style={{ color: '#000000', fontSize: 13, margin: '4px 0 0' }}>
                {milestoneInfo.closedDeals} صفقة مغلقة
              </p>
            </div>
            {milestoneInfo.nextMilestone && (
              <div style={{ textAlign: 'left' }}>
                <div style={{ color: '#64748b', fontSize: 12 }}>الهدف التالي</div>
                <div style={{ color: '#4ecdc4', fontSize: 20, fontWeight: 900 }}>
                  {milestoneInfo.nextMilestone.deals} صفقة
                </div>
                <div style={{ color: '#10b981', fontSize: 14, fontWeight: 700 }}>
                  +{milestoneInfo.nextMilestone.reward} AED
                </div>
              </div>
            )}
          </div>
          
          {/* Progress Bar */}
          <div style={{
            background: '#ffffff',
            borderRadius: 12,
            height: 32,
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid #334155',
          }}>
            <div style={{
              background: 'linear-gradient(90deg, #4ecdc4, #10b981)',
              height: '100%',
              width: `${milestoneInfo.progress}%`,
              transition: 'width 0.5s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontWeight: 900,
              fontSize: 14,
            }}>
              {milestoneInfo.progress.toFixed(0)}%
            </div>
          </div>
          
          {/* Milestone Markers */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            marginTop: 16,
            padding: '0 4px',
            gap: 8,
          }}>
            {milestoneInfo.allMilestones.slice(0, 5).map((m, idx) => {
              // First unreached milestone is always the "next" target
              const isNext = !m.reached && (idx === 0 || milestoneInfo.allMilestones[idx - 1]?.reached);
              const isFuture = !m.reached && !isNext;
              
              return (
                <div key={idx} style={{ 
                  textAlign: 'center',
                  minWidth: 70,
                  flex: 1,
                  position: 'relative',
                }}>
                  {/* Connecting Line */}
                  {idx < 4 && (
                    <div style={{
                      position: 'absolute',
                      top: 12,
                      right: '50%',
                      width: '100%',
                      height: 3,
                      background: m.reached ? '#10b981' : '#ffffff',
                      zIndex: 0,
                    }} />
                  )}
                  
                  {/* Milestone Circle */}
                  <div style={{ 
                    width: 40, 
                    height: 40, 
                    borderRadius: '50%', 
                    background: m.reached 
                      ? 'linear-gradient(135deg, #10b981, #059669)'
                      : isNext
                        ? 'linear-gradient(135deg, #f59e0b, #d97706)'
                        : 'linear-gradient(135deg, #374151, #4b5563)',
                    margin: '0 auto 6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    color: '#fff',
                    fontWeight: 900,
                    position: 'relative',
                    zIndex: 1,
                    border: m.reached ? '3px solid #34d399' : isNext ? '3px solid #fbbf24' : '2px solid #6b7280',
                    boxShadow: m.reached 
                      ? '0 0 20px rgba(16, 185, 129, 0.5)'
                      : isNext 
                        ? '0 0 20px rgba(245, 158, 11, 0.6), 0 0 40px rgba(245, 158, 11, 0.3)'
                        : '0 0 10px rgba(107, 114, 128, 0.2)',
                    animation: isNext ? 'pulse 2s infinite' : 'none',
                  }}>
                    {m.reached ? '🏆' : m.deals}
                  </div>
                  
                  {/* Deals Count */}
                  <div style={{ 
                    color: m.reached ? '#34d399' : isNext ? '#fbbf24' : '#64748b', 
                    fontSize: 11,
                    fontWeight: m.reached || isNext ? 900 : 600,
                    marginBottom: 2,
                  }}>
                    {m.deals} صفقة
                  </div>
                  
                  {/* Reward Amount */}
                  <div style={{ 
                    color: m.reached ? '#10b981' : isNext ? '#f59e0b' : '#475569', 
                    fontSize: 12, 
                    fontWeight: 900,
                    background: isNext ? 'rgba(245, 158, 11, 0.1)' : 'transparent',
                    padding: isNext ? '2px 6px' : '0',
                    borderRadius: isNext ? '4px' : '0',
                  }}>
                    {m.reward} AED
                  </div>
                  
                  {/* Status Label */}
                  {m.reached && (
                    <div style={{
                      color: '#10b981',
                      fontSize: 9,
                      fontWeight: 700,
                      marginTop: 2,
                    }}>
                      ✓ تم
                    </div>
                  )}
                  {isNext && (
                    <div style={{
                      color: '#f59e0b',
                      fontSize: 9,
                      fontWeight: 700,
                      marginTop: 2,
                      animation: 'pulse 2s infinite',
                    }}>
                      🔥 الهدف
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Milestone Earnings Summary */}
          {claimedMilestones.length > 0 && (
            <div style={{
              marginTop: 16,
              padding: 12,
              background: '#ffffff',
              borderRadius: 8,
              border: '1px solid #10b981',
            }}>
              <div style={{ color: '#6ee7b7', fontSize: 12 }}>
                إجمالي مكافآت الإنجازات: <span style={{ color: '#34d399', fontWeight: 900, fontSize: 16 }}>{milestoneEarnings.toFixed(2)} AED</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
        gap: 16, 
        marginBottom: 32 
      }}>
        {/* Commission Per Deal Card */}
        <div style={{
          background: "linear-gradient(135deg, #ffffff, #312e81)",
          border: "1px solid #6366f1",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#a5b4fc", fontSize: 12, marginBottom: 8 }}>العمولة لكل صفقة</p>
          <p style={{ color: "#818cf8", fontSize: 32, fontWeight: 900 }}>
            {currentUser?.commission_per_deal ? `${currentUser.commission_per_deal} AED` : '—'}
          </p>
          <p style={{ color: "#6366f1", fontSize: 11, marginTop: 4 }}>
            لكل صفقة ناجحة مغلقة
          </p>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #ffffff, #ffffff)",
          border: "1px solid #10b981",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#6ee7b7", fontSize: 12, marginBottom: 8 }}>إجمالي المدفوعات</p>
          <p style={{ color: "#34d399", fontSize: 32, fontWeight: 900 }}>
            {totalEarningsWithMilestones.toFixed(2)} AED
          </p>
          {milestoneEarnings > 0 && (
            <p style={{ color: "#10b981", fontSize: 11, marginTop: 4 }}>
              يشمل {milestoneEarnings.toFixed(0)} AED مكافآت إنجازات
            </p>
          )}
        </div>

        <div style={{
          background: "linear-gradient(135deg, #ffffff, #ffffff)",
          border: "1px solid #f59e0b",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#fcd34d", fontSize: 12, marginBottom: 8 }}>المعلقة</p>
          <p style={{ color: "#f59e0b", fontSize: 32, fontWeight: 900 }}>
            {pendingEarnings.toFixed(2)} AED
          </p>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #ffffff, #ffffff)",
          border: "1px solid #4ecdc4",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#93c5fd", fontSize: 12, marginBottom: 8 }}>عدد العمولات</p>
          <p style={{ color: "#4ecdc4", fontSize: 32, fontWeight: 900 }}>
            {userCommissions.length}
          </p>
        </div>
      </div>

      {/* Commissions Table */}
      <div style={{
        background: "#ffffff",
        borderRadius: 16,
        border: "1px solid #e2e8f0",
        overflow: "hidden"
      }}>
        <div style={{ padding: 20, borderBottom: "1px solid #e2e8f0" }}>
          <h2 style={{ color: "#000000", fontSize: 18, fontWeight: 700, margin: 0 }}>
            سجل العمولات
          </h2>
        </div>

        {userCommissions.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "#64748b" }}>
            <p style={{ fontSize: 48, marginBottom: 16 }}>💰</p>
            <p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد عمولات بعد</p>
            <p style={{ fontSize: 13, marginTop: 8 }}>
              ستظهر العمولات هنا عند إغلاق الصفقات
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#ffffff" }}>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    المطعم
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    المبلغ
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    الحالة
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    التاريخ
                  </th>
                </tr>
              </thead>
              <tbody>
                {userCommissions.map(comm => (
                  <tr key={comm.id} style={{ borderTop: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", color: "#000000" }}>
                      <div style={{ fontWeight: 700 }}>{comm.restaurant?.name || 'N/A'}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{comm.restaurant?.area || ''}</div>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#10b981", fontWeight: 700 }}>
                      {parseFloat(comm.amount).toFixed(2)} AED
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <StatusBadge 
                        status={comm.status === 'paid' ? 'deal_closed' : 'pending_closure'} 
                      />
                    </td>
                    <td style={{ padding: "12px 16px", color: "#000000", fontSize: 13 }}>
                      {new Date(comm.created_at).toLocaleDateString('ar-AE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
